import React, { useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Container, Box, Typography, Button, Paper, Link, Table, Tab, Tabs } from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import '../styles/individualPatient.css';
import Plot from 'react-plotly.js';

const toNumOrNull = (v) => {
    const s = String(v ?? "").trim();
    if (s === "") return null;           // makes Plotly leave a gap
    const n = Number(s);
    return Number.isFinite(n) ? n : null;
};

const boolToPassFail = (b) => b === "0" ? <p className="fail">Fail</p> : <p className="pass">Pass</p>;

function DiagnosticChart({ tests }) {
    let freqs, leftY, rightY, layout;

    freqs = [500, 1000, 2000, 4000, 6000, 8000];

    layout = {
        xaxis: { title: { text: "Diagnostic Test Frequencies (Hz)" } },
        yaxis: {
            title: "Result (dB HL)",
            range: [120, -10], // reversed order → -10 at top, 120 at bottom
            tickvals: [-10, 0, 20, 40, 60, 80, 100, 120],
        },
        height: 380,
        legend: { orientation: "h", y: -0.4 },
    };

    let leftTraces = [], rightTraces = [];

    for (const t of tests) {
        leftY = freqs.map((f) => toNumOrNull(t[`left_threshold_${f}`]));
        rightY = freqs.map((f) => toNumOrNull(t[`right_threshold_${f}`]));

        leftTraces.push({
            x: freqs,
            y: leftY,
            name: `L ${t.test_date}`,
            type: "scatter",
            mode: "lines+markers",
            marker: { color: "blue", size: 7, symbol: "x" },
            line: { color: "blue" }
        });

        rightTraces.push({
            x: freqs,
            y: rightY,
            name: `R ${t.test_date}`,
            type: "scatter",
            mode: "lines+markers",
            marker: { color: "red", size: 7, symbol: "circle-open" },
            line: { color: "red" }
        });

    }

    // 4) minimal layout so 0/1 reads as Fail/Pass
    const config = {
        responsive: true,
        displaylogo: false,
        modeBarButtonsToRemove: [
            'zoom2d', 'pan2d', 'select2d', 'lasso2d', 'zoomIn2d', 'zoomOut2d',
            'resetScale2d', // keep if you want it
            'hoverClosestCartesian', 'hoverCompareCartesian', 'toggleSpikelines'
        ],
        // We KEEP: 'autoScale2d' (autoscale button) and 'toImage' (download PNG)
    }


    return <div>
        <Plot data={[...leftTraces, ...rightTraces]} layout={layout} config={config} className="plot-container" />

    </div>;
}

const IndividualPatient = () => {
    const location = useLocation();
    const navigate = useNavigate();
    const patient = location.state?.patient;
    const patientTests = location.state?.patientTests ?? { screening: [], diagnostic: [] };
    const [screeningOrDiagnostic, setScreenOrDiagnostic] = useState(0);
    const [screeningDisplayTests, setScreeningDisplayTests] = useState([]);
    const [diagnosticDisplayTests, setDiagnosticDisplayTests] = useState([]);

    function handleChangeScreeningDisplayTest(test) {
        const index = screeningDisplayTests.indexOf(test);
        if (index > -1) {
            //Test already in array, remove test
            let newDisplayTests = screeningDisplayTests.filter((_, i) => i !== index);
            newDisplayTests.sort((a, b) => Date.parse(a.test_date) - Date.parse(b.test_date));
            setScreeningDisplayTests([...newDisplayTests]);
        } else {
            //Doesn't contain test, add test
            let newDisplayTests = [...screeningDisplayTests, test];
            newDisplayTests.sort((a, b) => Date.parse(a.test_date) - Date.parse(b.test_date));
            setScreeningDisplayTests([...newDisplayTests]);
        }
    }

    function handleChangeDiagnosticDisplayTest(test) {
        const index = diagnosticDisplayTests.indexOf(test);
        if (index > -1) {
            //Test already in array, remove test
            let newDisplayTests = diagnosticDisplayTests.filter((_, i) => i !== index);
            newDisplayTests.sort((a, b) => Date.parse(a.test_date) - Date.parse(b.test_date));
            setDiagnosticDisplayTests([...newDisplayTests]);
        } else {
            //Doesn't contain test, add test
            let newDisplayTests = [...diagnosticDisplayTests, test];
            newDisplayTests.sort((a, b) => Date.parse(a.test_date) - Date.parse(b.test_date));
            setDiagnosticDisplayTests([...newDisplayTests]);
        }
    }

    return (
        <Container maxWidth="md">
            <Box className="top-container">
                <Button
                    onClick={() => navigate(-1)}
                    startIcon={<ArrowBackIcon />}
                    variant="text"
                    className="back-button"
                >
                    Back to Results
                </Button>

                <Paper elevation={3} className="paper-container">
                    <Typography variant="h4" gutterBottom>
                        Patient Details
                    </Typography>

                    <Box className="section-spacing">
                        <Typography variant="h6" color="text.secondary" gutterBottom>
                            Name
                        </Typography>
                        <Typography variant="body1" className="text-spacing">
                            {patient.first_name} {patient.middle_name && `${patient.middle_name} `}{patient.last_name}
                        </Typography>

                        <Typography variant="h6" color="text.secondary" gutterBottom>
                            Date of Birth
                        </Typography>
                        <Typography variant="body1" className="text-spacing">
                            {patient.dob}
                        </Typography>

                        <Typography variant="h6" color="text.secondary" gutterBottom>
                            Sex
                        </Typography>
                        <Typography variant="body1" className="info-value">
                            {patient.sex || "N/A"}
                        </Typography>

                        <Typography variant="h6" color="text.secondary" gutterBottom>
                            Race / Ethnicity
                        </Typography>
                        <Typography variant="body1" className="info-value">
                            {patient.race || "N/A"}
                        </Typography>

                        <Typography variant="h6" color="text.secondary" gutterBottom>
                            Highest Educational Level
                        </Typography>
                        <Typography variant="body1" className="info-value">
                            {patient.highest_education_level || "N/A"}
                        </Typography>

                        <Typography variant="h6" color="text.secondary" gutterBottom>
                            Phone Number
                        </Typography>
                        <Typography variant="body1" className="text-spacing">
                            {patient.phone}
                        </Typography>

                        <Typography variant="h6" color="text.secondary" gutterBottom>
                            Address
                        </Typography>
                        <Typography variant="body1" className="text-spacing">
                            {patient.street_address}
                            {patient.city && <><br />{patient.city}, {patient.state} {patient.zip}</>}
                        </Typography>

                        <Tabs value={screeningOrDiagnostic} onChange={(e, v) => setScreenOrDiagnostic(v)} className="tabs-spacing">
                            <Tab label="Screening Tests" />
                            <Tab label="Diagnostic Tests" />
                        </Tabs>
                        {screeningOrDiagnostic === 0 && (
                            <div className='screening-container'>
                                {patientTests.screening?.map((t, i) =>
                                    <Typography
                                        className="block-text"
                                    >
                                        <input
                                            type="checkbox"
                                            name="screeningCheckbox"
                                            value={patientTests}
                                            onChange={e => handleChangeScreeningDisplayTest(t)}
                                        />
                                        {t.test_date}
                                    </Typography>
                                )}
                                <Typography variant="h6" color="text.secondary" gutterBottom>
                                    Test Data
                                </Typography>
                                <Table>
                                    <thead>
                                        <tr className={'table-head'}>
                                            <td>Test Date</td>
                                            <td>L 1000hz</td>
                                            <td>R 1000hz</td>
                                            <td>L 2000hz</td>
                                            <td>R 2000hz</td>
                                            <td>L 4000hz</td>
                                            <td>R 4000hz</td>
                                            <td>L 6000hz</td>
                                            <td>R 6000hz</td>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {screeningDisplayTests.map((t, i) => {
                                            return <tr key={i}>
                                                <td>
                                                    <Link
                                                        key={`screening-${i}`}
                                                        underline="hover"
                                                        className="test-link"
                                                        onClick={() => navigate('/individualPatientTest', { state: { patient: patient, test: t, isScreening: true } })}>
                                                        {t.test_date}
                                                    </Link>

                                                </td>
                                                <td>{boolToPassFail(t[`hz_1000_passed_left`])}</td>
                                                <td>{boolToPassFail(t[`hz_1000_passed_right`])}</td>
                                                <td>{boolToPassFail(t[`hz_2000_passed_left`])}</td>
                                                <td>{boolToPassFail(t[`hz_2000_passed_right`])}</td>
                                                <td>{boolToPassFail(t[`hz_4000_passed_left`])}</td>
                                                <td>{boolToPassFail(t[`hz_4000_passed_right`])}</td>
                                                <td>{boolToPassFail(t[`hz_6000_passed_left`])}</td>
                                                <td>{boolToPassFail(t[`hz_6000_passed_right`])}</td>
                                            </tr>
                                        })}
                                    </tbody>
                                </Table>
                            </div>
                        )}

                        {screeningOrDiagnostic === 1 && (
                            <div className='diagnostic-container'>
                                {patientTests.diagnostic?.map((t, i) =>
                                    <Typography
                                        className="block-text"
                                    //onClick={() => navigate('/individualPatientTest', { state: {patient: patient, test: t, isScreening: false } })}
                                    >
                                        <input
                                            type="checkbox"
                                            name="diagnosticCheckbox"
                                            value={patientTests.diagnostic}
                                            onChange={e => handleChangeDiagnosticDisplayTest(t)}
                                        />
                                        {t.test_date} <Tab />
                                        <Link
                                            key={`diagnostic-${i}`}
                                            underline="hover"
                                            onClick={() => navigate('/individualPatientTest', { state: { patient: patient, test: t, isScreening: false } })}>
                                            Individual Test
                                        </Link>
                                    </Typography>
                                )}
                                <Typography variant="h6" color="text.secondary" gutterBottom>
                                    Test Data
                                </Typography>
                                <DiagnosticChart tests={[...diagnosticDisplayTests]}></DiagnosticChart>
                            </div>
                        )}
                    </Box>
                </Paper>
            </Box>
        </Container>
    );
}

export default IndividualPatient;